import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Button, Cascader, Space, Tabs } from 'antd';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import useService from '@/lib/hooks/useService';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';
import AddReservationModal from '../AddReservationModal';
import ArrangementTable from '../ArrangementTable';
import styles from './index.module.scss';
import type { CarServiceReservation } from '@/types/database/carService';
import type { ReservedUser } from '../../types';
import type { SingleValueType } from 'rc-cascader/lib/Cascader';
import type { User } from '@/types/database/users';

dayjs.extend(advancedFormat);

export const UpdateReservationsContext = createContext<() => void>(() => {});

const ReservationList = () => {
  const [users] = useService<ReservedUser>({ collectionName: 'users' });

  const startOfTodayTimestamp = Number(dayjs().startOf('day').format('x'));

  const [carServiceData, updateData] = useService<CarServiceReservation>({
    collectionName: 'carService',
  });

  const [allReservations, setAllReservations] = useState<
    CarServiceReservation[]
  >([]);

  const [isOpenAddReservationModal, setIsOpenAddReservationModal] =
    useState<boolean>(false);
  const [tab, setTab] = useState<string>('待安排');
  const [selectedHouseholdNumber, setSelectedHouseholdNumber] =
    useState<SingleValueType>([]);

  const getLatestReservations = useCallback(() => {
    updateData();
  }, [updateData]);

  useEffect(() => {
    setAllReservations(carServiceData);
  }, [carServiceData]);

  useEffect(() => {
    if (selectedHouseholdNumber.length === 0) {
      getLatestReservations();
    }
  }, [getLatestReservations, selectedHouseholdNumber.length]);

  const getWaitArrangeReservations = () => {
    return allReservations.filter((reservation) => {
      const { reservationTime, status } = reservation;
      return (
        status === '已預約' &&
        dayjs(reservationTime).isAfter(startOfTodayTimestamp)
      );
    });
  };

  const getArrangedReservations = () => {
    return allReservations.filter((reservation) => {
      const { reservationTime, status } = reservation;
      return (
        status === '已安排' &&
        dayjs(reservationTime).isAfter(startOfTodayTimestamp)
      );
    });
  };

  const getHistoryReservations = () => {
    return allReservations.filter(
      (reservation) =>
        reservation.status === '已下車' || reservation.status === '已取消'
    );
  };

  const closeModal = () => {
    setIsOpenAddReservationModal(false);
  };

  const getReservedUsers = (
    reservationsForDisplay: CarServiceReservation[],
    users: ReservedUser[]
  ): ReservedUser[] => {
    const reservedUsers: ReservedUser[] = [];
    for (const reservation of reservationsForDisplay) {
      for (const index in users) {
        if (reservation.userId === users[index]._id) {
          reservedUsers.push(users[index]);
        }
      }
    }
    return reservedUsers;
  };

  const getWaitArrangeCascaderOptions = () => {
    const waitArrangeReservations = getWaitArrangeReservations();
    const reservedUsers = getReservedUsers(waitArrangeReservations, users);
    const householdNumbers = reservedUsers.map((user) => {
      return user.householdNumbers[0];
    });
    return generateHouseHoldNumberCascader(householdNumbers);
  };

  const getArrangedCascaderOptions = () => {
    const arrangedReservations = getArrangedReservations();
    const reservedUsers = getReservedUsers(arrangedReservations, users);
    const householdNumbers = reservedUsers.map((user) => {
      return user.householdNumbers[0];
    });
    return generateHouseHoldNumberCascader(householdNumbers);
  };

  const getHistoryCascaderOptions = () => {
    const historyReservations = getHistoryReservations();
    const reservedUsers = getReservedUsers(historyReservations, users);
    const householdNumbers = reservedUsers.map((user) => {
      return user.householdNumbers[0];
    });
    return generateHouseHoldNumberCascader(householdNumbers);
  };

  const displayCascaderOptions = (tab: string) => {
    switch (tab) {
      case '待安排':
        return getWaitArrangeCascaderOptions();

      case '已安排':
        return getArrangedCascaderOptions();

      default:
        return getHistoryCascaderOptions();
    }
  };

  const handleCascaderChange = (
    householdNumber: SingleValueType | undefined
  ) => {
    if (householdNumber === undefined) {
      getLatestReservations();
      setSelectedHouseholdNumber([]);
    } else {
      const filteredReservationsByHouseholdNumber = allReservations.filter(
        (reservation) => {
          const foundUser = users.find((user) => {
            return user._id === reservation.userId;
          });
          return foundUser?.householdNumbers[0] === householdNumber?.join('-');
        }
      );
      setAllReservations(filteredReservationsByHouseholdNumber);
      setSelectedHouseholdNumber(householdNumber);
    }
  };

  const displayRender = (label: string[]) => {
    return label.join('-');
  };

  const getReservationUserData = (
    reservation: CarServiceReservation,
    users: User[]
  ) => {
    return users.find((user) => user._id === reservation.userId);
  };

  const generateWaitArrangeReservationsForTable = () => {
    const waitArrangeReservations = getWaitArrangeReservations();
    return waitArrangeReservations.map((reservation) => {
      const { _id: key, createdTime, reservationTime } = reservation;
      const reservedUser = getReservationUserData(reservation, users);
      return {
        key,
        createdTime: dayjs(createdTime).format('YYYY-MM-DD HH:mm'),
        reservationTime: dayjs(reservationTime).format('YYYY-MM-DD HH:mm'),
        householdNumber: reservedUser?.householdNumbers
          ? reservedUser.householdNumbers[0]
          : '',
        name: reservedUser?.name,
        action: ['查看', '新增進度', '取消'],
      };
    });
  };
  const generateArrangedReservationsForTable = () => {
    const arrangedReservations = getArrangedReservations();
    return arrangedReservations.map((reservation) => {
      const {
        _id: key,
        createdTime,
        reservationTime,
        fleetName,
        carNumber,
      } = reservation;
      const reservedUser = getReservationUserData(reservation, users);
      return {
        key,
        createdTime: dayjs(createdTime).format('YYYY-MM-DD HH:mm'),
        reservationTime: dayjs(reservationTime).format('YYYY-MM-DD HH:mm'),
        householdNumber: reservedUser?.householdNumbers
          ? reservedUser.householdNumbers[0]
          : '',
        name: reservedUser?.name,
        fleetName,
        carNumber,
        action: ['查看', '取消'],
      };
    });
  };
  const generateHistoryReservationsForTable = () => {
    const historyReservations = getHistoryReservations();
    return historyReservations.map((reservation) => {
      const {
        _id: key,
        createdTime,
        reservationTime,
        status,
        fleetName,
      } = reservation;
      const reservedUser = getReservationUserData(reservation, users);
      return {
        key,
        createdTime: dayjs(createdTime).format('YYYY-MM-DD HH:mm'),
        reservationTime: dayjs(reservationTime).format('YYYY-MM-DD HH:mm'),
        householdNumber: reservedUser?.householdNumbers
          ? reservedUser.householdNumbers[0]
          : '',
        name: reservedUser?.name,
        status: status !== '已取消' ? '已結束' : status,
        fleetName,
        action: ['查看'],
      };
    });
  };

  const handleTabChange = (activeKey: string) => {
    setSelectedHouseholdNumber([]);
    setTab(activeKey);
  };

  return (
    <UpdateReservationsContext.Provider value={updateData}>
      <Space direction="vertical" size="large" className={styles.pageContainer}>
        <Button onClick={() => setIsOpenAddReservationModal(true)}>
          新增預約
        </Button>
        <AddReservationModal
          isOpenModal={isOpenAddReservationModal}
          closeModal={closeModal}
        />
        <Space>
          <p>戶號:</p>
          <Cascader
            options={displayCascaderOptions(tab)}
            placeholder="請選擇戶號"
            onChange={handleCascaderChange}
            displayRender={displayRender}
            value={selectedHouseholdNumber}
          />
        </Space>
        <Tabs
          defaultActiveKey="1"
          onChange={handleTabChange}
          items={[
            {
              label: `待安排`,
              key: '待安排',
              children: (
                <ArrangementTable
                  tab={tab}
                  dataSource={generateWaitArrangeReservationsForTable()}
                />
              ),
            },
            {
              label: `已安排`,
              key: '已安排',
              children: (
                <ArrangementTable
                  tab={tab}
                  dataSource={generateArrangedReservationsForTable()}
                />
              ),
            },
            {
              label: `歷史資料`,
              key: '歷史資料',
              children: (
                <ArrangementTable
                  tab={tab}
                  dataSource={generateHistoryReservationsForTable()}
                />
              ),
            },
          ]}
        />
      </Space>
    </UpdateReservationsContext.Provider>
  );
};

export default ReservationList;
